import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
import { format } from "d3-format";
import {formatNumber, FORMATTERS} from "./text.js";
import { setContext, getContext } from "svelte";

/**
 * @typedef {Object} Config
 */

let contextKey = Symbol("config");

export function getConfig() {
  return getContext(contextKey);
}

export function setConfig(config) {
  setContext(contextKey, config);
}

/**
 * @typedef {Object} ColumnConfig
 * @property {string} column_name
 * @property {string} column_label
 * @property {string} data_formatting
 * @property {string} scale_color
 * @property {string} scale_type
 * @property {string} scale_breaks
 * @property {string} scale_min_max
 * @property {(d: number) => string} format
 */

/**
 * @param {ColumnConfig[]} columnConfig
 * @param {string} accentColor
 */
export function createConfig(columnConfig, accentColor) {
  let parsedConfig = parseConfig(columnConfig, accentColor);
  return parsedConfig;
}

const baseConfig = {
  map: {
    stroke: urbanColors.white,
    hoverStroke: urbanColors.yellow,
    strokeWidth: 1,
    hoverStrokeWidth: 3,
    fontColor: urbanColors.gray_shade_darkest,
    fontWeight: "bold",
    fontSize: 18
  },
};

/**
 * @param {ColumnConfig[]} columnConfig
 * @param {string} accentColor
 */
function parseConfig(columnConfig, accentColor) {
  let columnMap = new Map();
  for (let col of columnConfig) {
    columnMap.set(col.column_name, parseColumnConfig(col));
  }
  let combinedConfig = Object.assign(baseConfig, { columns: columnMap });
  if (accentColor) {
    combinedConfig.map.hoverStroke = accentColor;
  }
  return combinedConfig;
}

/**
 * @param {ColumnConfig} columnConfig
 */
function parseColumnConfig(columnConfig) {
  let result = {
    column_name: columnConfig.column_name,
    column_label: columnConfig.column_label.trim()
      ? columnConfig.column_label
      : columnConfig.column_name,
    data_formatting: columnConfig.data_formatting,
    scale_color: columnConfig.scale_color.trim() === "" ? "blue" : columnConfig.scale_color,
    scale_type: columnConfig.scale_type === "" ? "linear" : columnConfig.scale_type,
    scale_breaks:
      columnConfig.scale_breaks.trim() === ""
        ? undefined
        : parseArrayStr(columnConfig.scale_breaks),
    scale_min_max:
      columnConfig.scale_min_max.trim() === ""
        ? undefined
        : parseArrayStr(columnConfig.scale_min_max)
  };
  let defaultFormats = Object.keys(FORMATTERS);
  if (defaultFormats.includes(result.data_formatting)) {
    result.format = (d) => formatNumber(d, result.data_formatting);
  } else {
    result.format = (d) => format(result.data_formatting)(d) || "N/A";
  }
  return result;
}

/**
 * @param {string | undefined} arrayStr
 * @returns {number[] | undefined}
 */
function parseArrayStr(arrayStr) {
  return arrayStr ? arrayStr.split(",").map((str) => Number(str.trim())) : undefined;
}
